import React from 'react';
import Card from '../common/Card';
import Badge from '../common/Badge';
import SafeIcon from '../../common/SafeIcon';
import ReactECharts from 'echarts-for-react';
import { motion } from 'framer-motion';

const DashboardView = ({ inventory, alerts }) => {
  const totalValue = inventory.reduce((acc, item) => acc + (item.stock * item.unitCost), 0);
  const stockoutCount = inventory.filter(i => i.stock === 0).length;

  const chartOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', backgroundColor: '#1a1a1a', borderColor: '#333', textStyle: { color: '#eee' } },
    grid: { top: 30, right: 20, bottom: 30, left: 40 },
    xAxis: {
      type: 'category',
      data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      axisLine: { lineStyle: { color: '#333' } },
      axisLabel: { color: '#888', fontFamily: 'JetBrains Mono' }
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: '#1a1a1a', type: 'dashed' } },
      axisLabel: { color: '#888', fontFamily: 'JetBrains Mono' }
    },
    series: [
      {
        name: 'Fulfillment Vol.',
        data: [120, 132, 101, 134, 90, 230, 210],
        type: 'line',
        smooth: true,
        lineStyle: { color: '#ccff00', width: 3 },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [{ offset: 0, color: 'rgba(204,255,0,0.3)' }, { offset: 1, color: 'rgba(204,255,0,0)' }]
          }
        },
        symbol: 'circle',
        symbolSize: 8,
        itemStyle: { color: '#ccff00', borderColor: '#000', borderWidth: 2 }
      }
    ]
  };

  const MetricCard = ({ title, value, subtext, icon, alert }) => (
    <Card className="relative overflow-hidden group">
      <div className="absolute -right-6 -top-6 text-sp-gray-800 opacity-50 group-hover:text-sp-gray-700 transition-colors duration-500">
        <SafeIcon name={icon} style={{ fontSize: '100px' }} />
      </div>
      <div className="relative z-10">
        <div className="text-gray-400 text-sm mb-2 uppercase tracking-wider">{title}</div>
        <div className={`text-3xl font-mono font-bold mb-1 ${alert ? 'text-sp-alert drop-shadow-neon-alert' : 'text-white'}`}>
          {value}
        </div>
        <div className="text-xs text-gray-500 font-mono">{subtext}</div>
      </div>
    </Card>
  );

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard 
          title="Total Inventory Value" 
          value={`$${totalValue.toLocaleString()}`} 
          subtext="Across all zones" 
          icon="DollarSign" 
        />
        <MetricCard 
          title="Active Alerts" 
          value={alerts.length} 
          subtext="Items below min. threshold" 
          icon="AlertTriangle" 
          alert={alerts.length > 0}
        />
        <MetricCard 
          title="Stockouts" 
          value={stockoutCount} 
          subtext="Critical action required" 
          icon="XOctagon" 
          alert={stockoutCount > 0}
        />
        <MetricCard 
          title="Pending POs" 
          value="8" 
          subtext="Expected within 14 days" 
          icon="Truck" 
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title="Demand Forecast vs Actuals" className="lg:col-span-2 min-h-[300px]">
          <ReactECharts option={chartOptions} style={{ height: '280px', width: '100%' }} />
        </Card>

        <Card title="Priority Action Queue" className="lg:col-span-1">
          <div className="space-y-3">
            {alerts.slice(0,4).map((item, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-sp-gray-800 rounded border border-sp-gray-700 hover:border-sp-alert/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${item.stock === 0 ? 'bg-sp-alert animate-pulse' : 'bg-orange-500'}`}></div>
                  <div>
                    <div className="text-sm text-gray-200 font-medium truncate w-32">{item.sku}</div>
                    <div className="text-xs text-gray-500 font-mono">Stock: {item.stock} / {item.minStock}</div>
                  </div>
                </div>
                <button className="text-xs bg-sp-gray-900 border border-sp-gray-700 hover:border-sp-lime hover:text-sp-lime px-2 py-1 rounded transition-colors uppercase font-mono">
                  Review
                </button>
              </div>
            ))}
            {alerts.length === 0 && (
              <div className="text-center py-8 text-gray-500 font-mono text-sm">
                <SafeIcon name="CheckCircle" className="text-3xl text-sp-lime/50 mx-auto mb-2" />
                ALL SYSTEMS NOMINAL
              </div>
            )}
          </div>
        </Card>
      </div>
    </motion.div>
  );
};

export default DashboardView;